import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import classNames from 'classnames';

import SliderContainer, { Slide } from '../global/Slider';
import ProductCardContainer from './ProductCard';
import ProductCollectionItem from './ProductCollectionItem';
import { getWindowWidth } from '../../store/selectors/ui.selectors';

function Arrow(props) {
  const {
    className,
    side,
    onClick,
    topOffset,
  } = props;

  return onClick ? (
    <div
      className={classNames(className, 'product-carousel-slider__arrow', `product-carousel-slider__arrow--${side}`)}
      onClick={onClick}
      style={topOffset ? { top: topOffset } : undefined}
    />
  ) : null;
}

export class ProductCarouselSlider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      viewed: false,
      currentSlide: 0,
      sliderImageHeight: 0,
    };

    this.sliderRef = createRef();

    this.handleWaypointEnter = this.handleWaypointEnter.bind(this);
    this.getInnerSliderProps = this.getInnerSliderProps.bind(this);
    this.triggerCarouselViewedCallbacks = this.triggerCarouselViewedCallbacks.bind(this);
    this.afterSlideChange = this.afterSlideChange.bind(this);
    this.onCarouselReInit = this.onCarouselReInit.bind(this);
    this.renderArrow = this.renderArrow.bind(this);
    this.filterProductCollectionItemsVisibilityByDeviceType = this.filterProductCollectionItemsVisibilityByDeviceType.bind(this);
  }

  itemViewedCallbacks = [];

  componentDidMount() {
    this.triggerCarouselViewedCallbacks();
  }

  componentWillUnmount() {
    this.setState({
      viewed: false,
    });
    this.itemViewedCallbacks = null;
  }

  handleWaypointEnter() {
    if (!this.state.viewed) {
      this.setState({ viewed: true });
    }
    this.triggerCarouselViewedCallbacks();
  }

  getInnerSliderProps() {
    return this.sliderRef?.current?.getBreakpointsProps?.() || {};
  }

  triggerCarouselViewedCallbacks() {
    const {
      viewed,
      currentSlide,
    } = this.state;
    const {
      slidesPerGroup,
    } = this.getInnerSliderProps();

    if (!viewed) {
      return;
    }

    for (let i = currentSlide; i < Math.round(currentSlide + slidesPerGroup); i += 1) {
      const cb = this.itemViewedCallbacks?.[i];
      if (cb) {
        cb();
      }
    }
  }

  afterSlideChange(current) {
    this.setState({
      currentSlide: current,
    }, () => {
      this.triggerCarouselViewedCallbacks();
    });
  }

  filterProductCollectionItemsVisibilityByDeviceType() {
    const {
      productCollectionItems,
      windowWidth,
    } = this.props;

    const isTablet = windowWidth < 1024;
    const isMobile = windowWidth < 640;

    let productCollectionItemsFiltered = productCollectionItems;

    if (isMobile) {
      productCollectionItemsFiltered = productCollectionItems.filter(
        (productCollectionItem) => !productCollectionItem.visibilities?.includes('HiddenMobile'),
      );
    } else if (isTablet) {
      productCollectionItemsFiltered = productCollectionItems.filter(
        (productCollectionItem) => !productCollectionItem.visibilities?.includes('HiddenTablet'),
      );
    } else {
      productCollectionItemsFiltered = productCollectionItems.filter(
        (productCollectionItem) => !productCollectionItem.visibilities?.includes('HiddenDesktop'),
      );
    }

    return productCollectionItemsFiltered;
  }

  renderProductCollectionItems() {
    const { history } = this.props;
    const productCollectionItemsFiltered = this.filterProductCollectionItemsVisibilityByDeviceType();

    return productCollectionItemsFiltered.map((collectionItem, i) => (
      <Slide key={`carousel-collection-item-${collectionItem.id}`}>
        <ProductCollectionItem
          history={history}
          collectionItem={collectionItem}
          item={collectionItem.item}
          source={'product-carousel'}
          hideDetails={collectionItem.hideDetails}
          hideShoppingLink={collectionItem.hideShoppingLink}
          hideColorPicker={collectionItem.hideColorPicker}
          productCarouselLifestyleCard={true}
          setItemViewedCallback={(cb) => {
            this.itemViewedCallbacks[i] = cb;
          }}
        />
      </Slide>
    ));
  }

  renderProductCards() {
    const {
      products,
      history,
    } = this.props;

    return products.map((product, i) => (
      <Slide key={`carousel-product-card-${product}`}>
        <ProductCardContainer
          history={history}
          position={i}
          name={'size'}
          overflow={true}
          source={'product-carousel'}
          sku={product}
        />
      </Slide>
    ));
  }

  onCarouselReInit() {
    const sliderElement = this.sliderRef?.current?.swiper.el;
    const firstSliderLink = sliderElement?.getElementsByTagName('a')[0];
    const firstSliderImage = firstSliderLink?.getElementsByTagName('img')[0];
    const firstSliderImageHeight = firstSliderImage?.getBoundingClientRect()?.height;

    if (this.state.sliderImageHeight !== firstSliderImageHeight) {
      this.setState({ sliderImageHeight: firstSliderImageHeight });
    }
  }

  renderArrow(side, className, onClick) {
    const { sliderImageHeight } = this.state;
    const arrowTopOffset = sliderImageHeight / 2;
    return (
      <Arrow
        className={className}
        side={side}
        topOffset={arrowTopOffset}
        onClick={onClick}
      />
    );
  }

  renderSlider() {
    const {
      productCollectionItems,
      products,
      columnWidths = [],
      disableInfiniteSlider,
    } = this.props;

    const productCollectionItemsAvailable = Array.isArray(productCollectionItems) && productCollectionItems.length > 0;
    const isNarrow = columnWidths?.includes('desktop_8');
    const columns = productCollectionItemsAvailable ? productCollectionItems.length : products.length;
    return (
      <SliderContainer
        ref={this.sliderRef}
        className={classNames(
          'product-carousel-slider__scroll',
          { narrow: isNarrow },
        )}
        slidesPerView={1.25}
        slidesPerGroup={1}
        disableInfinite={disableInfiniteSlider}
        pagination={false}
        breakpoints={{
          640: {
            slidesPerView: 2.75,
            slidesPerGroup: 2,
          },
          1024: {
            slidesPerView: isNarrow || columns > 3 ? 3.25 : 3,
            slidesPerGroup: 3,
          },
        }}
        onReInit={this.onCarouselReInit}
        renderArrow={this.renderArrow}
        onChange={this.afterSlideChange}
      >
        {/* Render ProductCard or LifestyleCard if productCollectionItem is available */}
        {productCollectionItemsAvailable
          ? this.renderProductCollectionItems()
          : this.renderProductCards()
        }
      </SliderContainer>
    );
  }

  render() {
    return (
      <Waypoint onEnter={this.handleWaypointEnter}>
        <div className="product-carousel-slider__wrapper">
          <div
            className={classNames('product-carousel-slider', {
              'product-carousel-slider--transition': this.state.viewed,
            })}
          >
            {this.renderSlider()}
          </div>
        </div>
      </Waypoint>
    );
  }
}

const mapStateToProps = (state) => ({
  windowWidth: getWindowWidth(state),
});

export default connect(mapStateToProps, null)(ProductCarouselSlider);
